import { useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { SearchParam } from '@common/ts/enums';
import useTranslation from '@/hooks/useTranslation';
import { useAnalysisSchemaStatus } from '@/reactQuery/researchAssistant';
import OutdatedAnalysisDialog from '@/components/Modals/OutdatedAnalysisDialog';
import ComparisonHub from './ComparisonHub';
import ComparisonReport from './ComparisonReport';

interface CompareTabProps {
  analysisLanguage: string;
  onReRun?: (filterId?: string | null) => Promise<void>;
  isRerunLoading?: boolean;
}

function CompareTab({ analysisLanguage, onReRun, isRerunLoading }: CompareTabProps) {
  const { lang } = useTranslation();
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const comparisonId = searchParams.get(SearchParam.COMPARISON_ID);

  // Pre-flight: does this project have the rich analysis schema the
  // Compare feature needs? Check is project-level (no filter) because
  // Compare targets multiple filters and the gate applies to the base
  // analysis pipeline. If the schema is current, we render normally;
  // otherwise we show the re-run dialog so the user can upgrade.
  const { data: schemaStatus, refetch: refetchSchemaStatus } = useAnalysisSchemaStatus(
    projectId,
    null,
  );
  // Local "dismissed" state lets the user click Cancel and still see
  // the hub (even though compare-attempt will fail). Defaults to false
  // so a fresh mount re-prompts. Resetting on refetch is handled by
  // the useEffect below.
  const [dialogDismissed, setDialogDismissed] = useState(false);

  const showOutdatedDialog =
    !!schemaStatus && !schemaStatus.current && !dialogDismissed && !isRerunLoading;

  if (comparisonId) {
    return <ComparisonReport comparisonId={comparisonId} analysisLanguage={analysisLanguage} />;
  }

  return (
    <>
      {isRerunLoading && (
        <div className="max-w-3xl mx-auto px-6 pt-6">
          <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
            <Loader2 className="h-4 w-4 animate-spin shrink-0" />
            {lang.get('msg.analysisInProgressCompare')}
          </div>
        </div>
      )}
      <ComparisonHub analysisLanguage={analysisLanguage} onReRun={onReRun} isRerunLoading={isRerunLoading} />
      {showOutdatedDialog && (
        <OutdatedAnalysisDialog
          isOpen={showOutdatedDialog}
          reason={schemaStatus.reason || 'old_schema'}
          feature="compare"
          projectId={projectId}
          filterId={null}
          onCancel={() => setDialogDismissed(true)}
          onReRunComplete={() => {
            // The re-run started successfully. Dismiss the dialog and
            // refetch the status so when analysis finishes, the dialog
            // stays gone. The ComparisonHub's compare-attempt button
            // still gates via error-response-handling in case the user
            // clicks before the re-run finishes.
            setDialogDismissed(true);
            refetchSchemaStatus();
          }}
        />
      )}
    </>
  );
}

export default CompareTab;
